import { NudgeObject } from "@grow-therapy-team/seedling-components";
import { format, millisecondsToHours, differenceInYears } from "date-fns";
import { SyncMapItem } from "twilio-sync";
import { getVisitorPresenceUpdateIntervalMs } from "../config";
import {
  ParticipantStatus,
  VisitorPresence,
  VisitorPresenceMap,
} from "../types";
import { olderThan } from "../utils";
import type { RemoteParticipant } from "../video-sdk";
import { PatientName, ProviderDrawerState, SelectedVisitor } from "./state";

export const DEFAULT_SAFETY_GUARD_AGE = 13;

export function filterActive(v: VisitorPresenceMap) {
  const filtered = Object.entries(v).filter(
    ([_, { timestamp }]) =>
      // Allow a single missed heartbeat
      !olderThan(timestamp, getVisitorPresenceUpdateIntervalMs() * 2),
  );
  return Object.fromEntries(filtered);
}

export function mapItemToVisitor(
  item: SyncMapItem,
): VisitorPresenceMap | undefined {
  if (!("name" in item.data) || !("timestamp" in item.data)) return;
  const data = item.data as VisitorPresence;
  const { key: sid } = item;
  const {
    moodCheckinAt,
    emotions,
    name,
    timestamp,
    patientShortId,
    waitingRoomConversationSid,
    appPlatform,
    videoPlatform,
    admissionTime,
    joinRoomMode,
  } = data;

  return {
    [sid]: {
      moodCheckinAt,
      emotions,
      name,
      timestamp,
      patientShortId,
      waitingRoomConversationSid,
      appPlatform,
      videoPlatform,
      admissionTime,
      joinRoomMode,
    },
  };
}

export function mapItemsToVisitors(items: SyncMapItem[]) {
  return items
    .filter((s) => "name" in s.data && "timestamp" in s.data)
    .map(mapItemToVisitor)
    .reduce<VisitorPresenceMap>((acc, v) => ({ ...acc, ...v }), {});
}

/**
 * returns the difference between the start and end timestamps in `HH:MM:SS`
 * format, omitting `HH:` if 0.
 *
 * e.g. `timeDifference(new Date("2023-08-24T14:46:30Z").getTime(), new
 * Date("2023-08-24T14:46:31Z").getTime())` returns `00:01`.
 */
export function timeDifference(startTimestamp: number, endTimestamp: number) {
  const differenceMs = endTimestamp - startTimestamp;
  const hours = millisecondsToHours(differenceMs);
  const minutesAndSeconds = format(differenceMs, "mm:ss");

  return `${hours ? `${hours}:` : ""}${minutesAndSeconds}`;
}

export function getSelectedClientInfo(
  selectedClientId: string | undefined,
  waitingVisitors: VisitorPresenceMap,
  participants: Record<string, RemoteParticipant>,
  selectedPatientPreferredName: PatientName | undefined,
  inSessionParticipant: SelectedVisitor | undefined,
  scheduledPatientInformation: SelectedVisitor | undefined,
  nudges?: NudgeObject[],
) {
  const visitor = waitingVisitors[selectedClientId || ""];
  const sessionIsInProgress = Object.values(participants).length > 0;
  const participantIsInSession = selectedClientId
    ? participants[selectedClientId]
    : false;
  const visitorIsWaiting = !!visitor;
  const selectedClientMatchesScheduledPatient =
    scheduledPatientInformation?.patientShortId === selectedClientId;

  if (visitorIsWaiting) {
    return {
      ...visitor,
      preferredName: selectedPatientPreferredName,
      status: ParticipantStatus.WAITING,
      visitorUuid: selectedClientId,
      nudges,
    };
    // In the case of multi-participant sessions where the participant whose clientId is selected leaves but other participants remain, still show the saved participant information
  } else if (participantIsInSession || sessionIsInProgress) {
    return {
      ...inSessionParticipant,
      preferredName: selectedPatientPreferredName,
      status: ParticipantStatus.IN_SESSION,
      nudges,
    };
  } else if (selectedClientMatchesScheduledPatient) {
    return {
      ...scheduledPatientInformation,
      preferredName: selectedPatientPreferredName,
      status: ParticipantStatus.SCHEDULED,
      nudges,
    };
  } else {
    return undefined;
  }
}

export function getIsClientInfoDrawerOpen(
  drawerState?: ProviderDrawerState | null,
) {
  return drawerState === ProviderDrawerState.CLIENT_INFORMATION;
}

export function getIsClientListDrawerOpen(
  drawerState?: ProviderDrawerState | null,
) {
  return drawerState === ProviderDrawerState.CLIENTS;
}

export function isValidDate(dateString: string): boolean {
  // Validate date format ( MM/DD/YYYY or YYYY-MM-DD or ISO 8601 format)
  const dateFormatRegex =
    /^\d{2}\/\d{2}\/\d{4}$|^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/;
  if (!dateFormatRegex.test(dateString)) {
    return false;
  }

  const date = new Date(dateString);

  // Check if the date is valid
  return !isNaN(date.getTime());
}

export function getIsClientUserUnderSafetyGuardAge(
  readableDob: string | null | undefined,
  safetyGuardAge: number = DEFAULT_SAFETY_GUARD_AGE,
) {
  if (!readableDob || !isValidDate(readableDob)) return false;

  const dob = new Date(readableDob);
  const today = new Date();

  const age = differenceInYears(today, dob);

  return age < safetyGuardAge;
}
